import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { SALESFORCE_CHART_COLORS } from "../lib/salesforceDashboard";
import {
  buildReportTable,
  type ReportCell,
  type ReportFilterPath,
  type ReportPage,
  reportTableLabels,
  type ReportTableOptions,
  type ReportTableRow,
} from "../lib/salesforceReportTable";
import { space } from "../theme/designTokens";

const colors = SALESFORCE_CHART_COLORS;
const CHECKBOX_ICON_SIZE_PX = 18;
const textSx = { fontSize: "13px", color: colors.text } as const;

const rowSx = (kind: ReportTableRow["kind"]) => {
  switch (kind) {
    case "group":
      return { fontWeight: 700 };
    case "subtotal":
      return { fontWeight: 700, backgroundColor: colors.headerBackground };
    case "total":
      return { fontWeight: 700, backgroundColor: colors.headerBackground, borderTop: `2px solid ${colors.cardBorder}` };
    case "detail":
      return {};
  }
};

export const ReportCellContent = ({ cell }: { cell: ReportCell }) => {
  const translate = useTranslate();
  switch (cell.kind) {
    case "empty":
      return null;
    case "text":
      return <>{cell.text}</>;
    case "link":
      return (
        <Link component={RouterLink} sx={{ color: colors.link }} to={cell.to} underline="hover">
          {cell.text}
        </Link>
      );
    case "lines":
      return (
        <>
          {cell.lines.map((line, index) => (
            // The lines are written once and never reordered.
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index}>{line}</Box>
          ))}
        </>
      );
    case "boolean": {
      const Icon = cell.checked ? CheckBoxOutlinedIcon : CheckBoxOutlineBlankIcon;
      return (
        <Icon
          aria-hidden={false}
          aria-label={translate(cell.checked ? "salesforceReports.report.yes" : "salesforceReports.report.no")}
          role="img"
          sx={{ display: "block", width: `${CHECKBOX_ICON_SIZE_PX}px`, height: `${CHECKBOX_ICON_SIZE_PX}px` }}
        />
      );
    }
  }
};

const MAX_CELL_WIDTH_PX = 280;

// Body cells stay on one line, as in Lightning; a longer text is cut with an ellipsis and kept in full as a title.
const bodyCellSx = {
  maxWidth: `${MAX_CELL_WIDTH_PX}px`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const cellSx = {
  ...textSx,
  padding: "6px 12px",
  borderBottom: `1px solid ${colors.cardBorder}`,
  verticalAlign: "top",
} as const;

export const ReportTableView = ({
  page,
  options,
  filterPath,
}: {
  page: ReportPage;
  options: ReportTableOptions;
  filterPath: ReportFilterPath;
}) => {
  const translate = useTranslate();
  const table = useMemo(
    () => buildReportTable(page, reportTableLabels(translate, page), options, filterPath),
    [filterPath, options, page, translate]
  );

  if (table.rows.length === 0) {
    return (
      <Typography sx={{ ...textSx, p: `${space.lg}px` }}>{translate("salesforceReports.report.empty")}</Typography>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: "100%", width: "auto" }}>
        <TableHead>
          {table.headers.map((headerRow, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={rowIndex}>
              {headerRow.map(headerCell => (
                <TableCell
                  colSpan={headerCell.colSpan}
                  key={headerCell.key}
                  rowSpan={headerCell.rowSpan}
                  scope="col"
                  sx={{
                    ...cellSx,
                    backgroundColor: colors.headerBackground,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {headerCell.to ? (
                    <Link component={RouterLink} sx={{ color: colors.link }} to={headerCell.to} underline="hover">
                      {headerCell.text}
                    </Link>
                  ) : (
                    headerCell.text
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.rows.map(row => (
            <TableRow key={row.key} sx={rowSx(row.kind)}>
              {row.cells.map((cell, index) => (
                <TableCell
                  // The columns are fixed for the table.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  sx={{
                    ...cellSx,
                    ...bodyCellSx,
                    fontWeight: "inherit",
                    textAlign: cell.kind === "text" && cell.align === "right" ? "right" : "left",
                  }}
                  title={cell.kind === "text" || cell.kind === "link" ? cell.text : undefined}
                >
                  <ReportCellContent cell={cell} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
